import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchUsers,
  updateUser,
  updateUserStatus,
  createUser,
} from "../actions/userActions";

export interface User {
  id: string;
  email: string;
  name?: UserName;
  is_admin: boolean;
  phone_number_verified: boolean;
  email_verified?: boolean;
  is_active?: boolean; // DEPRECATED
  is_waitlisted?: boolean; // DEPRECATED
  stytch_user_id?: string;
  organization_id?: string;
  phone_number?: string;
  deactivation_reason?: string;
  is_developer: boolean;
  status: UserStatus;
  environment: string;
  scopes?: string[];
  roles?: UserRole[];
}

export interface UserName {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  first_name_phonetic?: string;
  middle_name_phonetic?: string;
  last_name_phonetic?: string;
}

export enum UserStatus {
  ACTIVE = "active",
  WAITLISTED = "waitlisted",
  SUSPENDED = "suspended",
  BANNED = "banned",
  ONBOARDING = "onboarding",
}

export enum UserRole {
  ADMIN = "admin",
  USER = "user",
  ANNOTATOR = "annotator",
}

interface UsersState {
  users: User[];
  loading: boolean;
  loadingNewUser: boolean;
  error: string | null;
}

const initialState: UsersState = {
  users: [],
  loading: false,
  loadingNewUser: false,
  error: null,
};

const testSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<UsersState>) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch data";
      })
      .addCase(updateUser.pending, (state) => {
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const updatedUser: User = action.payload;
        const idx = state.users.findIndex((u) => u.id === updatedUser.id);
        if (idx !== -1) {
          state.users[idx] = updatedUser;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.error.message || "Failed to update user";
      })
      .addCase(
        updateUserStatus.fulfilled,
        (state, action: PayloadAction<User>) => {
          const index = state.users.findIndex(
            (user) => user.id === action.payload.id
          );
          if (index !== -1) {
            state.users[index] = action.payload;
          }
        }
      )
      .addCase(createUser.rejected, (state, action) => {
        state.error = action.error.message || "Failed to create user";
        state.loadingNewUser = false;
      })
      .addCase(createUser.pending, (state) => {
        state.loadingNewUser = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.users.push(action.payload);
        state.loadingNewUser = false;
        state.error = null;
      });
  },
});

export default testSlice.reducer;
